import axios from 'axios';
import { API_URL } from './api';

/**
 * Публичные эндпоинты рекламы. main.urls смонтированы на /api/,
 * рекламный роутер подключён внутри них через include.
 */
export const ADS_PUBLIC_BASE = `${API_URL}/api/ads`;

const pendingImpressions = new Map();
const openingClicks = new Set();

const authHeaders = (token) => (
    token
        ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        : { 'Content-Type': 'application/json' }
);

/**
 * Все методы «мягкие»: при любой ошибке возвращают безопасное значение
 * и пишут в консоль. Реклама не имеет права ронять экран.
 */

export const APIAdsServe = async (token, { placement, count = 1, sessionId = '' }) => {
    try {
        const r = await axios.post(
            `${ADS_PUBLIC_BASE}/serve/`,
            { placement, count, session_id: sessionId },
            { headers: authHeaders(token), timeout: 6000 },
        );
        return r.data; // { success, placement, ads: [] }
    } catch (e) {
        console.warn('Ad serve failed:', e?.message);
        return { success: true, placement: null, ads: [] };
    }
};

export const APIAdsTrackImpression = (token, adToken) => {
    if (pendingImpressions.has(adToken)) return pendingImpressions.get(adToken);
    const promise = axios.post(
        `${ADS_PUBLIC_BASE}/track/impression/`, { token: adToken },
        { headers: authHeaders(token), timeout: 6000 },
    ).then(r => r.data).catch(e => {
        console.warn('Ad impression tracking failed:', e?.message);
        return { success: false };
    });
    pendingImpressions.set(adToken, promise);
    promise.finally(() => {
        if (pendingImpressions.get(adToken) === promise) pendingImpressions.delete(adToken);
    });
    return promise;
};


export const APIAdsTrackEvent = async (token, adToken, kind, payload = null) => {
    try {
        const r = await axios.post(
            `${ADS_PUBLIC_BASE}/track/event/`,
            { token: adToken, kind, payload },
            { headers: authHeaders(token), timeout: 6000 },
        );
        return r.data;
    } catch (e) {
        console.warn('Ad event tracking failed:', e?.message);
        return { success: false };
    }
};

/**
 * Ссылка перехода. Всегда через click_url — серверный редирект работает
 * при заблокированном JS и в новой вкладке, поэтому это основной способ
 * засчитывать клики. Параметр d — миллисекунды от показа до клика,
 * он участвует в антифрод-проверке.
 */
export const buildClickUrl = (ad, dwellMs) => {
    if (!ad?.click_url) return null;
    const separator = ad.click_url.includes('?') ? '&' : '?';
    const dwell = Number.isFinite(dwellMs) ? Math.max(0, Math.round(dwellMs)) : null;
    return dwell === null ? ad.click_url : `${ad.click_url}${separator}d=${dwell}`;
};

/**
 * Открытие перехода. В новой вкладке: увести человека из приложения
 * на сайт рекламодателя — значит потерять сессию ради одного клика.
 */
export const openAdClick = async (ad, dwellMs) => {
    const url = buildClickUrl(ad, dwellMs);
    if (!url || openingClicks.has(ad.token)) return;
    openingClicks.add(ad.token);
    let win = null;
    try {
        // Открываем окно синхронно по нажатию. noopener в window.open может
        // вернуть null даже при успешном открытии — это не сигнал блокировки.
        win = window.open('about:blank', '_blank');
        if (win) win.opener = null;
        // Не регистрируем искусственный показ по самому нажатию.
        // Только дожидаемся уже отправленного подтверждения видимости.
        const pending = pendingImpressions.get(ad.token);
        if (pending) await pending;
        if (win && !win.closed) win.location.replace(url);
        else window.location.assign(url);
    } catch (_) {
        if (win && !win.closed) win.close();
        window.location.assign(url);
    } finally {
        setTimeout(() => openingClicks.delete(ad.token), 1500);
    }
};